import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { collection, onSnapshot, query, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { STATUS_COLORS } from "../theme";

const STATUS_OPTIONS = ["all","pending","matched","accepted","en_route","arrived","in_progress","completed","couldnt_fix","needs_revisit","cancelled"];

export function JobsView() {
  const [jobs, setJobs] = useState<any[]>([]);
  const [techMap, setTechMap] = useState<Record<string, string>>({});
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  // Load all technicians once for name lookup
  useEffect(() => {
    getDocs(collection(db, "technicians")).then(snap => {
      const map: Record<string, string> = {};
      snap.docs.forEach(d => {
        const data = d.data();
        map[d.id] = data.displayName || data.name || data.email || d.id.slice(0, 8) + "…";
      });
      setTechMap(map);
    });
  }, []);

  useEffect(() => {
    return onSnapshot(
      query(collection(db, "jobs"), orderBy("createdAt", "desc"), limit(100)),
      snap => setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, []);

  const filtered = jobs
    .filter(j => filterStatus === "all" || j.status === filterStatus)
    .filter(j => !search || j.id.includes(search) || j.category?.includes(search));

  const fmt = (ts: any) => {
    if (!ts?.toDate) return "—";
    return ts.toDate().toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search job ID or category…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            {STATUS_OPTIONS.map(s => (
              <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                {s === "all" ? "All Statuses" : s.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
          {filtered.length} jobs
        </Typography>
      </Box>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Job ID","Status","Category","Emergency","Total","Platform Fee","Tech","Created"].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(job => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                      {job.id.slice(0,8)}…
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={job.status?.replace(/_/g," ") || "pending"}
                      size="small"
                      sx={{ bgcolor: STATUS_COLORS[job.status] || "#555", color: "white", fontWeight: 700, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                      {job.category?.replace(/_/g," ") || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {job.isEmergency ? <Chip label="🚨 Yes" size="small" color="error" /> : <Typography variant="caption" color="text.secondary">No</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {job.totalCharge != null ? `$${Number(job.totalCharge).toFixed(2)}` : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="secondary.main" fontWeight={600}>
                      {job.platformFee != null ? `$${Number(job.platformFee).toFixed(2)}` : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(job.technicianId || job.techId) ? (
                      <Typography variant="body2" fontWeight={600}>
                        {job.techName ||
                          techMap[job.technicianId] ||
                          techMap[job.techId] ||
                          (job.technicianId || job.techId)?.slice(0,8)+"…"}
                      </Typography>
                    ) : (
                      <Chip label="Unassigned" size="small" sx={{ bgcolor: "rgba(107,114,128,0.15)", color: "#9CA3AF", fontSize: 11 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {fmt(job.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                    No jobs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
